import {
	useState,
	useEffect,
	useRef
} from "react";
import {
	Navigate,
	useParams,
	useNavigate
} from "react-router-dom";
import {
	Box,
	Typography,
	Container,
	Grid,
	Paper,
	Skeleton,
	IconButton,
	TextField,
	Checkbox,
	Button,
	Tooltip,
	CircularProgress,
	TableContainer,
	Table,
	TableRow,
	TableHead,
	TableBody,
	TableCell,
	FormControlLabel,
	Divider,
	Switch,
} from '@mui/material';
import { LoadingButton } from '@mui/lab'
import SectionTitle from '../../components/common/SectionTitle'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import CurrentClientContext from '../../providers/CurrentClientProvider'
import ClientService from '../../services/ClientService'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PlaylistAddCheckCircleOutlinedIcon from '@mui/icons-material/PlaylistAddCheckCircleOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import moment from 'moment'
import TipsContext from '../../providers/GlobalTip'
import EmptyIcon from '../../components/icons/EmptyIcon'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker, DatePicker } from '@mui/x-date-pickers';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import DataFileInfo from '../../components/client/uploadData/DataFileInfo'
import DataFileService from '../../services/DataFileService'
import constant from '../../constants/constant'
import SettingsIcon from '@mui/icons-material/Settings';
import SettingModal from '../../components/client/uploadData/SettingModal'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import TableauReport from "../../components/tableau/TableauReport";


const jss = {
	changeLogItem: {
		display:'flex',
		p:1,
		border:'1px solid #ccc',
		borderRadius:1,
		mt:1,
		transition: 'all 0.3s',
		info: {
			flex:1,
			wordBreak:'break-all',
			cursor:'pointer',
		},
		name: {
			whiteSpace:"wrap",
			fontSize:13,
		},
		extra: {
			fontSize:11,
			mt:0.5,
		},
		checkIcon: {
			ml:1,
			cursor:'pointer',
		},
		":hover": {
			backgroundColor: '#f3f4f5',
		},
		'&.checked': {
			backgroundColor: '#dbcfe6',
			color:'primary.main',
			borderColor: 'primary.main'
		},
	},
	emptySelectLog: {
		textAlign:'center', 
		py:15, 
		color:'text.disabled', 
		fontSize:30
	},
	changeLogSection: {
		title: {
			fontSize:16,
			fontWeight:'bold',
			my:2
		},
		actionSection: {
			p:2,
			backgroundColor: '#eee9f3',
			display:'flex',
			alignItems:'center',
		},
	},
}

function UploadData(props) {
	const fileInputRef = useRef()
	const [dataFiles, setDataFiles] = useState([])
	const [loading, setLoading] = useState(true)
	const [uploading, setUploading] = useState(false)
	const [processing, setProcessing] = useState(false)
	const [loadingSelect, setLoadingSelect] = useState(false)
	const currentClientContext = CurrentClientContext.useClient()
	const [targetDataFile, setTargetDataFile] = useState()
	const [changeLogSummary, setChangeLogSummary] = useState([])
	const tip_context = TipsContext.useMessage()
	const [uploadDataForm, setUploadDataForm] = useState({
		isEndOfMonth: false,
		dataThroughDate: moment(),
	})
	const [settingOpen, setSettingOpen] = useState(false)

	useEffect(() => {
		if( currentClientContext.client ) {
			loadDataFiles()
		}
	}, [currentClientContext.client])

	async function loadDataFiles() {
		const _files = await DataFileService.getClientDataFiles(currentClientContext.client.Id)
		setLoading(false)
		setDataFiles(_files)
	}

	async function selectDataFile(dataFile) {
		setLoadingSelect(true)
		setTargetDataFile(dataFile)
		setLoadingSelect(false)
	}

	function chooseFile() {
		fileInputRef.current.click()
	}

	async function uploadDataFile(e) {
		if( e.target.files.length==0 ) {
			return
		}
		try {
			setUploading(true)
			let data = {
				is_month_to_date: uploadDataForm.isEndOfMonth ? 0 : 1,
			}
			if( !uploadDataForm.isEndOfMonth ) {
				data['data_through_date'] = uploadDataForm.dataThroughDate.format('YYYY-MM-DD 00:00:00')
			}
			const new_data_file = await DataFileService.uploadDataFile(currentClientContext.client.Id, e.target.files[0], data)
			dataFileChangeHandler()
		} catch(e) {
			tip_context.show(e.response.data.message)
		}
		setUploading(false)
	}

	function dataFileChangeHandler(_updatedDataFile) {
		loadDataFiles()
		if( _updatedDataFile ) {
			setTargetDataFile(_updatedDataFile)	
		}
	}

	function dataFileDeleteHandler(_deletedDataFile) {
		loadDataFiles()
		setTargetDataFile(null)
	}

	function openSettingModal() {
		setSettingOpen(true)
	}

	function closeSettingModal() {
		setSettingOpen(false)
	}

	function backup() {
		DataFileService.clientDataFileBackup(currentClientContext.client.Id)
	}

	async function syncCloudData() {
		try {
			await DataFileService.syncCloudData(currentClientContext.client.Id)
		} catch(e) {
		}
		tip_context.showSuccess()
	}
	// const currentEpoch = ClientService.getEpochMonthFromDate(new Date());


	return <Box sx={{py:2}}>
		<Typography variant="h5">Import Data</Typography>
		<Grid container spacing={2} sx={{mt:0}}>
			<Grid item xs={12} sm={3}>
				<Paper elevation={2}>
					<SectionTitle 
						label="Import Data"
						rightComponent={
							<>
								<Tooltip title="Sync cloud data">
									<IconButton onClick={syncCloudData}>
										<CloudSyncIcon />
									</IconButton>
								</Tooltip>
								<Tooltip title="Download Data Template">
									<IconButton component="a" href={`${constant.BACKEND_DOMAIN}/clients/${currentClientContext.client?.Id}/data-files/download`} target="_blank">
										<DownloadRoundedIcon />
									</IconButton>
								</Tooltip>
							</>
						}
					/>
					<Box >
						<Box sx={{p:1.5}}>
							<FormControlLabel control={<Switch value={uploadDataForm.isEndOfMonth} onChange={e => setUploadDataForm({...uploadDataForm, isEndOfMonth: e.target.checked})} />} label="Is end of month?" />
							{!uploadDataForm.isEndOfMonth &&
								<LocalizationProvider dateAdapter={AdapterDateFns}>
									<DesktopDatePicker
										label="Data through date"
										value={uploadDataForm.dataThroughDate}
										inputFormat="MMM dd"
										mask=""
										onChange={e => setUploadDataForm({...uploadDataForm, dataThroughDate: moment(e)})}
										renderInput={(params) => <TextField {...params}
											fullWidth={true}
											variant="outlined"
											size="small"
											sx={{mt:2}} 
										/>}
									/>
								</LocalizationProvider>
							}
							<Box 
								component="input" 
								type='file' 
								name="data" 
								sx={{display:'none'}} 
								ref={fileInputRef}
								onChange={uploadDataFile}
								onClick={(event)=> {
									event.target.value = null
								}}
							></Box>
							<LoadingButton loading={uploading} variant="contained" fullWidth startIcon={<CloudUploadRoundedIcon />} onClick={chooseFile} sx={{mt:2}}>Upload File</LoadingButton>
						</Box>
						<Divider sx={{mt:1}}/>
						<Box sx={{p:1.5}}>
							<Box sx={{
								display:'flex', 
								justifyContent:'space-between',
								alignItems: 'center',
							}}>
								<Typography variant="body1">Data Files</Typography>
								<Box>
									<Tooltip title="Backup">
										<IconButton onClick={backup}>
											<CloudDownloadOutlinedIcon />
										</IconButton>
									</Tooltip>
									<Tooltip title="Adjust inventory">
										<IconButton onClick={openSettingModal}>
											<SettingsIcon />
										</IconButton>
									</Tooltip>
									<SettingModal 
										client={currentClientContext.client}
										open={settingOpen}
										onClose={closeSettingModal}
										onRefreshDataFiles={dataFileChangeHandler}
									/>
								</Box>
							</Box>
							<Box sx={{maxHeight:'80vh',overflowY:'scroll'}}>
							{dataFiles?.length>0 && dataFiles.map(_file => {
								return <Box key={`data_file_${_file.ClientDataFileId}`} sx={jss.changeLogItem} className={targetDataFile?.ClientDataFileId==_file.ClientDataFileId ? 'checked' : ''} >
									<Box sx={jss.changeLogItem.info} onClick={e => selectDataFile(_file)}>
										<Box sx={jss.changeLogItem.name}>
											<b>{moment(_file.Period, 'YYYYMM').format('MMM YYYY')}</b>
											<Box component="span" sx={{ml:1}}>{_file.FileName}</Box>
										</Box>
										<Box sx={jss.changeLogItem.extra}>
											{_file.IsMonthToDate==1 && <>
												{moment(_file.data_through_date).format('M/DD')}
											</>}
											{_file.IsMonthToDate==0 && <>
												End of Month
											</>}
											{_file.ProcessedDate &&
												<Box component="span" sx={{ml:1}}>Updated: {moment(_file.ProcessedDate).format('M/DD HH:mm')}</Box>
											}
										</Box>
									</Box>
                                    {!!_file.is_month_to_date && 
                                    	<>MTD</>
                                    }
									{!_file.is_month_to_date && <>
										{!!_file.finalized_date && <>
											{!!_file.is_planned && <>
												<LockIcon sx={jss.changeLogItem.checkIcon}/>
											</>}
											{!_file.is_planned && <>
												<LockOutlinedIcon sx={jss.changeLogItem.checkIcon}/>
											</>}
										</>}
										{!_file.finalized_date && <>
											<LockOpenIcon sx={jss.changeLogItem.checkIcon}/>
										</>}
									</>}
								</Box>
							})}
							{loading && <Box sx={{textAlign:'center',pt:2}}>
								<CircularProgress color="primary" />
							</Box>
							}
							{dataFiles?.length===0 && !loading && 
								<Box sx={{py:2,textAlign:'center',color:'text.disabled'}}><EmptyIcon sx={{fontSize:60}}/></Box>
							}
							</Box>
						</Box>
					</Box>
				</Paper>
			</Grid>
			<Grid item xs={12} sm={9}>
				<Paper elevation={2}>
					{(!targetDataFile && !loadingSelect) && 
						<Box sx={jss.emptySelectLog}>
							Please select a data file
						</Box>
					}
					{loadingSelect && 
						<Box sx={jss.emptySelectLog}>
							<CircularProgress color="primary" />
						</Box>
					}
					{targetDataFile && currentClientContext && currentClientContext.client && (
						<>
							<TableauReport epochMonth={ClientService.getEpochMonthFromDate(moment(targetDataFile.Period + '01').toDate())} clientId={currentClientContext.client.Id} url={'https://data.retailorbit.com/trusted/{user_auth_key}/views/clientDataView_QA/Multi-Location?:showAppBanner=false&:toolbar=top&:display_count=n&:showVizHome=n&:origin=viz_share_link'} height="100vh" />
						</>
						// <DataFileInfo
						// 	dataFile={targetDataFile}
						// 	onDataFileChange={dataFileChangeHandler}
						// 	onDataFileDelete={dataFileDeleteHandler}
						// />
					)}
				</Paper>
			</Grid>
		</Grid>
	</Box>
}
export default UploadData